import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Card,
  Badge,
  InputGroup,
  Accordion,
  Spinner,
} from "react-bootstrap";
import {
  doc,
  setDoc,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import {
  FaUser,
  FaIdCard,
  FaCalendar,
  FaMoneyBillWave,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaShieldAlt,
  FaBriefcase,
  FaUserTie,
  FaMapMarkerAlt,
  FaGift,
} from "react-icons/fa";

// Agrega estas funciones auxiliares después de las importaciones
const formatearFecha = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const parsearFecha = (fechaStr) => {
  if (!fechaStr) return null;
  const [day, month, year] = fechaStr.split("/");
  return new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
};

const normalizarFechaParaInputDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();

    // Ya viene en formato ISO para input date (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    // Formato DD/MM/YYYY -> YYYY-MM-DD
    const ddmmyyyy = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month}-${day}`;
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const y = parsed.getUTCFullYear();
      const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
      const d = String(parsed.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    return "";
  }

  if (value?.seconds) {
    const date = new Date(value.seconds * 1000);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getUTCFullYear();
    const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
    const d = String(parsed.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  return "";
};

const OPCIONES_PAISES = [
  { value: "604", label: "Perú" },
  { value: "032", label: "Argentina" },
  { value: "068", label: "Bolivia" },
  { value: "076", label: "Brasil" },
  { value: "152", label: "Chile" },
  { value: "170", label: "Colombia" },
  { value: "218", label: "Ecuador" },
  { value: "600", label: "Paraguay" },
  { value: "858", label: "Uruguay" },
  { value: "862", label: "Venezuela" },
];

const OPCIONES_REGIMEN_LABORAL = [
  { value: "01", label: "Régimen laboral privado general" },
];

const OPCIONES_CATEGORIA_TRABAJADOR = [
  { value: "01", label: "Obrero" },
  { value: "02", label: "Empleado" },
  { value: "03", label: "Trabajador de dirección" },
];

const OPCIONES_NIVEL_EDUCATIVO = [
  { value: "03", label: "Primaria completa" },
  { value: "05", label: "Secundaria completa" },
  { value: "11", label: "Técnica completa" },
  { value: "12", label: "Universitaria incompleta" },
  { value: "13", label: "Universitaria completa" },
];

const OPCIONES_TIPO_CONTRATO = [
  { value: "01", label: "Indeterminado" },
  { value: "02", label: "Sujeto a modalidad" },
  { value: "04", label: "Tiempo parcial" },
];

const OPCIONES_PERIODICIDAD_INGRESOS = [
  { value: "1", label: "Mensual" },
];

const OCUPACIONES_SUNAT_FALLBACK = [
  { codigo: "251101", descripcion: "Analista de sistemas" },
  { codigo: "251102", descripcion: "Programador de sistemas" },
  { codigo: "251103", descripcion: "Ingeniero de software" },
  { codigo: "252201", descripcion: "Administrador de base de datos" },
  { codigo: "252301", descripcion: "Especialista en redes" },
  { codigo: "252901", descripcion: "Especialista en seguridad informática" },
  { codigo: "334101", descripcion: "Asistente administrativo" },
  { codigo: "331201", descripcion: "Contador" },
  { codigo: "332301", descripcion: "Asistente contable" },
  { codigo: "432101", descripcion: "Auxiliar de oficina" },
];



const db = getFirestore(firebaseApp);
const toSafeText = (value) => String(value ?? "").trim();

const EmpleadoForm = ({ show, onHide, empleado, usuario, onSave }) => {
  const initialState = {
    // Información del Trabajador
    tipo_documento: "DNI",
    dni: "",
    nombres: "",
    apellidos: "",
    sexo: "",
    fecha_nacimiento: "",
    domicilio: "",
    nacionalidad: "",
    fecha_ingreso: "",
    fecha_inicio_contrato: "",
    fecha_fin_contrato: "",
    fecha_reingreso: "",
    fecha_cese: "",
    cargo: "",
    ocupacion_cod: "251101", // Tabla 10 SUNAT (Default Analista Sistemas)
    pais_emisor_cod: "604",
    nacionalidad_cod: "604",
    regimen_laboral_cod: "01",
    categoria_trabajador_cod: "01",
    nivel_educativo_cod: "05",
    discapacidad_cod: "0",
    sctr_salud_cod: "0",
    sctr_pension_cod: "0",
    tipo_contrato_cod: "01",
    regimen_jornada_laboral_cod: "1", // 1=Sí cumple jornada máxima, 0=No
    sujeto_fiscalizacion_horario_cod: "1", // 1=Sí sujeto a control horario, 0=No
    horario_nocturno_cod: "0", // 1=Sí, 0=No
    sindicalizado_cod: "1",
    periodicidad_ingresos_cod: "1",
    percibe_renta5ta_cod: "0",
    ocupacion_publica_cod: "0",
    categoria_periodo_per_cod: "01",

    // Datos de pensión
    regimen_pensionario: "ONP",
    afp: "",
    cuspp: "",
    fecha_termino_comision_afp: "", // Nuevo campo

    // Datos salariales
    sueldo_base: "",
    asignacion_familiar: false,

    // Datos organizacionales
    centro_costo: "ADMINISTRATIVO",
    categoria: "",
    area: "",

    // Datos bancarios
    cuenta_bancaria: "",
    banco: "",

    // Datos de contacto
    email: "",
    telefono: "",

    // Seguros
    essalud_vida_importe: "",
    vida_ley_importe: "",
    sctr: "",
    fecha_EMO_inicio: "",
    fecha_EMO_fin: "",
    fecha_OEMO_inicio: "",
    fecha_OEMO_fin: "",
    fecha_brevete_fin: "",

    // Estado y empresa
    activo: true,
    ruc_empresa: usuario.ruc,

    // Nuevo campo: Régimen de trabajo
    regimen_trabajo_id: "",

    // Bonos asignados
    bonos_asignados: [],
  };

  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [regimenes, setRegimenes] = useState([]);
  const [bonosGlobales, setBonosGlobales] = useState([]);
  const [ocupacionesCatalogo, setOcupacionesCatalogo] = useState(OCUPACIONES_SUNAT_FALLBACK);
  const [filtroOcupacion, setFiltroOcupacion] = useState("");
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [selectedUsuarioDni, setSelectedUsuarioDni] = useState("");

  const normalizarUsuario = (raw = {}, fallbackId = "") => ({
    dni: toSafeText(raw?.dni || raw?.id || fallbackId),
    nombres: toSafeText(raw?.name || raw?.nombres || raw?.nombre),
    apellidos: toSafeText(raw?.apellidos || raw?.apellido),
    email: toSafeText(raw?.email),
    telefono: toSafeText(raw?.phone || raw?.telefono || raw?.celular),
    domicilio: toSafeText(raw?.direccion || raw?.domicilio),
    cargo: toSafeText(raw?.cargo),
    area: toSafeText(raw?.area),
    sexo: toSafeText(raw?.sexo),
    centro_costo: toSafeText(raw?.cecoNombre || raw?.ceco || raw?.centro_costo),
  });

  const cargarUsuariosEmpresa = async () => {
    if (!usuario?.ruc) return;
    try {
      setLoadingUsuarios(true);
      const byRucEmpresa = query(collection(db, "users"), where("rucEmpresa", "==", usuario.ruc));
      const byRuc = query(collection(db, "users"), where("ruc", "==", usuario.ruc));
      const [snapRucEmpresa, snapRuc] = await Promise.all([getDocs(byRucEmpresa), getDocs(byRuc)]);

      const merged = new Map();
      [...snapRucEmpresa.docs, ...snapRuc.docs].forEach((docRef) => {
        const normalizado = normalizarUsuario(docRef.data(), docRef.id);
        if (!normalizado.dni) return;
        merged.set(normalizado.dni, normalizado);
      });

      const rows = Array.from(merged.values()).sort((a, b) =>
        `${a.apellidos} ${a.nombres}`.localeCompare(`${b.apellidos} ${b.nombres}`, "es", { sensitivity: "base" })
      );
      setUsuariosDisponibles(rows);
    } catch (error) {
      console.error("Error cargando usuarios para autocompletar empleado:", error);
      setUsuariosDisponibles([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const fetchOcupacionesSunat = async () => {
    try {
      const snap = await getDocs(collection(db, "pl_tabla10_ocupaciones"));
      if (snap.empty) return;
      const data = snap.docs
        .map((d) => d.data())
        .filter((item) => item?.codigo && item?.descripcion)
        .map((item) => ({
          codigo: String(item.codigo).trim(),
          descripcion: String(item.descripcion).trim(),
        }));
      if (data.length > 0) {
        data.sort((a, b) => a.codigo.localeCompare(b.codigo));
        setOcupacionesCatalogo(data);
      }
    } catch (error) {
      console.warn("No se pudo cargar pl_tabla10_ocupaciones, usando catálogo base.", error);
    }
  };

  useEffect(() => {
    if (!show) return;

    // Cargar regímenes de trabajo de la empresa
    const fetchRegimenes = async () => {
      if (!usuario?.ruc) return;
      const q = query(
        collection(db, "pl_regimen_trabajo"),
        where("ruc", "==", usuario.ruc)
      );
      const snap = await getDocs(q);
      setRegimenes(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchRegimenes();

    // Cargar bonos globales activos
    const fetchBonosGlobales = async () => {
      const q = query(
        collection(db, "pl_bonos"),
        where("estado", "==", "activo"),
        where("ruc", "==", usuario.ruc)
      );
      const snap = await getDocs(q);
      setBonosGlobales(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchBonosGlobales();
    fetchOcupacionesSunat();
  }, [show, usuario]);

  const ocupacionesFiltradas = ocupacionesCatalogo.filter((item) => {
    const q = filtroOcupacion.toLowerCase().trim();
    if (!q) return true;
    return item.codigo.includes(q) || item.descripcion.toLowerCase().includes(q);
  });
  const ocupacionSeleccionada = ocupacionesCatalogo.find(
    (item) => item.codigo === String(formData.ocupacion_cod || "")
  );
  const ocupacionesVisibles = ocupacionSeleccionada &&
    !ocupacionesFiltradas.some((item) => item.codigo === ocupacionSeleccionada.codigo)
    ? [ocupacionSeleccionada, ...ocupacionesFiltradas]
    : ocupacionesFiltradas;

  useEffect(() => {
    if (empleado) {
      // Eliminar codigo_afiliado si viene de datos antiguos
      const { codigo_afiliado, ...rest } = empleado;
      // Formatear fechas al cargar
      const formattedData = {
        ...initialState,
        ...rest,
        fecha_nacimiento: formatearFecha(rest.fecha_nacimiento),
        fecha_ingreso: formatearFecha(rest.fecha_ingreso),
        fecha_inicio_contrato: normalizarFechaParaInputDate(
          rest.fecha_inicio_contrato ||
            rest.fechaInicioContrato ||
            rest.contratoFechaInicio
        ),
        fecha_fin_contrato: normalizarFechaParaInputDate(
          rest.fecha_fin_contrato ||
            rest.fechaFinContrato ||
            rest.contratoFechaFin ||
            rest.vencimientoContrato
        ),
        fecha_EMO_inicio: normalizarFechaParaInputDate(
          rest.fecha_EMO_inicio
        ),
        fecha_EMO_fin: normalizarFechaParaInputDate(
          rest.fecha_EMO_fin ||
            rest.vencimientoEMO
        ),
        fecha_OEMO_inicio: normalizarFechaParaInputDate(
          rest.fecha_OEMO_inicio
        ),
        fecha_OEMO_fin: normalizarFechaParaInputDate(
          rest.fecha_OEMO_fin ||
            rest.vencimientoLevantamientoObservacionMedico ||
            rest.vencimientoLevantamientoObservacionMedica ||
            rest.levantamientoObservacionMedicoVencimiento
        ),
        fecha_brevete_fin: normalizarFechaParaInputDate(
          rest.fecha_brevete_fin ||
            rest.vencimientoLicenciaConducir ||
            rest.licenciaConducirVencimiento ||
            rest.vencimiento_licencia_conducir
        ),
        fecha_reingreso: formatearFecha(rest.fecha_reingreso),
        fecha_cese: formatearFecha(rest.fecha_cese),
        fecha_termino_comision_afp: formatearFecha(
          rest.fecha_termino_comision_afp
        ),
      };
      setFormData(formattedData);
    } else {
      setFormData(initialState);
      setSelectedUsuarioDni("");
    }
    // eslint-disable-next-line
  }, [empleado]);

  useEffect(() => {
    if (!show || empleado) return;
    cargarUsuariosEmpresa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, empleado, usuario?.ruc]);

  const importarUsuarioSeleccionado = () => {
    if (!selectedUsuarioDni) return;
    const dataUsuario = usuariosDisponibles.find((item) => item.dni === selectedUsuarioDni);
    if (!dataUsuario) return;

    setFormData((prev) => ({
      ...prev,
      dni: dataUsuario.dni || prev.dni,
      nombres: dataUsuario.nombres || prev.nombres,
      apellidos: dataUsuario.apellidos || prev.apellidos,
      email: dataUsuario.email || prev.email,
      telefono: dataUsuario.telefono || prev.telefono,
      domicilio: dataUsuario.domicilio || prev.domicilio,
      cargo: dataUsuario.cargo || prev.cargo,
      area: dataUsuario.area || prev.area,
      sexo: dataUsuario.sexo || prev.sexo,
      centro_costo: dataUsuario.centro_costo || prev.centro_costo,
    }));

    Swal.fire({
      icon: "success",
      title: "Datos cargados",
      text: "Se importaron los datos básicos del usuario seleccionado.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData({
    ...formData,
    [name]: type === 'checkbox' ? checked : value,
  });
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones básicas
      if (!formData.dni || !formData.nombres || !formData.apellidos) {
        throw new Error("Los campos DNI, nombres y apellidos son obligatorios");
      }

      if (!formData.fecha_nacimiento) {
        throw new Error("La fecha de nacimiento es obligatoria para el T-Registro");
      }

      if (formData.regimen_pensionario === "AFP") {
        const cuspp = String(formData.cuspp || '').trim();
        if (cuspp.length !== 12) {
          throw new Error("El CUSPP debe tener exactamente 12 caracteres para trabajadores de AFP");
        }
      }

      // Parsear fechas antes de guardar
      const dataToSave = {
        ...formData,
        // Compatibilidad con módulos que leen campos históricos de vencimientos
        vencimientoEMO: formData.fecha_EMO_fin || "",
        vencimientoLevantamientoObservacionMedico: formData.fecha_OEMO_fin || "",
        vencimientoLicenciaConducir: formData.fecha_brevete_fin || "",
        fecha_nacimiento: parsearFecha(formData.fecha_nacimiento),
        fecha_ingreso: parsearFecha(formData.fecha_ingreso),
        fecha_reingreso: parsearFecha(formData.fecha_reingreso),
        fecha_cese: parsearFecha(formData.fecha_cese),
        fecha_termino_comision_afp: parsearFecha(
          formData.fecha_termino_comision_afp
        ),
      };

      const empleadoRef = doc(db, "pl_empleados", formData.dni);
      await setDoc(empleadoRef, {
        ...dataToSave,
        updated_at: new Date(),
        created_at: formData.created_at || new Date(),
      });

      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: `Empleado ${
          empleado ? "actualizado" : "registrado"
        } correctamente`,
      });

      onSave && onSave();
      onHide();
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Funciones para bonos asignados
  const handleBonoAsignadoChange = (index, field, value) => {
    const updatedBonos = [...formData.bonos_asignados];
    updatedBonos[index][field] = value;
    setFormData({ ...formData, bonos_asignados: updatedBonos });
  };

  const addBonoAsignado = () => {
    setFormData({
      ...formData,
      bonos_asignados: [
        ...(formData.bonos_asignados || []),
        { bonoId: "", monto: 0 },
      ],
    });
  };

  const removeBonoAsignado = (index) => {
    const updatedBonos = formData.bonos_asignados.filter((_, i) => i !== index);
    setFormData({ ...formData, bonos_asignados: updatedBonos });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header
        closeButton
        className="text-white py-3"
        style={{
          background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
          borderBottom: "3px solid #D2691E",
        }}
      >
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaUserTie size={20} />
          {empleado ? "Editar Empleado" : "Nuevo Empleado"}
          {empleado && (
            <Badge bg="light" text="dark" className="ms-2">
              {empleado.dni}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body
          className="p-4"
          style={{
            backgroundColor: "#f8f9fa",
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {!empleado && (
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                  <FaUser size={16} /> Importar Desde Usuarios
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-2 align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="mb-1">Selecciona un usuario de esta empresa</Form.Label>
                      <Form.Select
                        value={selectedUsuarioDni}
                        onChange={(e) => setSelectedUsuarioDni(e.target.value)}
                        disabled={loadingUsuarios || !usuariosDisponibles.length}
                      >
                        <option value="">
                          {loadingUsuarios
                            ? "Cargando usuarios..."
                            : usuariosDisponibles.length
                              ? "Seleccione usuario"
                              : "No hay usuarios disponibles"}
                        </option>
                        {usuariosDisponibles.map((item) => (
                          <option key={item.dni} value={item.dni}>
                            {item.apellidos} {item.nombres} · DNI {item.dni}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        onClick={cargarUsuariosEmpresa}
                        disabled={loadingUsuarios}
                      >
                        {loadingUsuarios ? <Spinner size="sm" className="me-1" /> : null}
                        Recargar
                      </Button>
                      <Button
                        variant="success"
                        onClick={importarUsuarioSeleccionado}
                        disabled={!selectedUsuarioDni}
                      >
                        Jalar datos
                      </Button>
                    </div>
                  </Col>
                </Row>
                <small className="text-muted d-block mt-2">
                  Se autocompletan DNI, nombres, apellidos, correo, teléfono, domicilio, cargo y área.
                </small>
              </Card.Body>
            </Card>
          )}

          {/* Información Personal */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaUser size={16} /> Información Personal
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Tipo de Documento</Form.Label>
                    <Form.Select
                      name="tipo_documento"
                      value={formData.tipo_documento}
                      onChange={handleChange}
                    >
                      <option value="DNI">DNI</option>
                      <option value="CE">CE</option>
                      <option value="PASAPORTE">PASAPORTE</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaIdCard className="me-1" />
                      DNI
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="dni"
                      value={formData.dni}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Nombres</Form.Label>
                    <Form.Control
                      type="text"
                      name="nombres"
                      value={formData.nombres}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Apellidos</Form.Label>
                    <Form.Control
                      type="text"
                      name="apellidos"
                      value={formData.apellidos}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Sexo</Form.Label>
                    <Form.Select
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleChange}
                    >
                      <option value="">Seleccione</option>
                      <option value="M">Masculino</option>
                      <option value="F">Femenino</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha de Nacimiento
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="fecha_nacimiento"
                      value={formData.fecha_nacimiento}
                      onChange={handleChange}
                      placeholder="DD/MM/YYYY"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaMapMarkerAlt className="me-1" />
                      Domicilio
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="domicilio"
                      value={formData.domicilio}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Información Laboral */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaBriefcase size={16} /> Información Laboral
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha de Ingreso
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="fecha_ingreso"
                      value={formData.fecha_ingreso}
                      onChange={handleChange}
                      placeholder="DD/MM/YYYY"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Fecha de Reingreso</Form.Label>
                    <Form.Control
                      type="text"
                      name="fecha_reingreso"
                      value={formData.fecha_reingreso}
                      onChange={handleChange}
                      placeholder="DD/MM/YYYY"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Fecha de Cese</Form.Label>
                    <Form.Control
                      type="text"
                      name="fecha_cese"
                      value={formData.fecha_cese}
                      onChange={handleChange}
                      placeholder="DD/MM/YYYY"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Régimen de Trabajo</Form.Label>
                    <Form.Select
                      name="regimen_trabajo_id"
                      value={formData.regimen_trabajo_id}
                      onChange={handleChange}
                    >
                      <option value="">Seleccione régimen</option>
                      {regimenes.map((regimen) => (
                        <option key={regimen.id} value={regimen.id}>
                          {regimen.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha Inicio de Contrato
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_inicio_contrato"
                      value={formData.fecha_inicio_contrato}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha Fin de Contrato
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_fin_contrato"
                      value={formData.fecha_fin_contrato}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Cargo</Form.Label>
                    <Form.Control
                      type="text"
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Ocupación (Tabla 10 SUNAT)</Form.Label>
                    <Form.Control
                      type="text"
                      value={filtroOcupacion}
                      onChange={(e) => setFiltroOcupacion(e.target.value)}
                      placeholder="Buscar ocupación por código o descripción..."
                      className="mb-2"
                    />
                    <Form.Select
                      name="ocupacion_cod"
                      value={formData.ocupacion_cod}
                      onChange={handleChange}
                    >
                      {ocupacionesVisibles.length > 0 ? (
                        ocupacionesVisibles.map((item) => (
                          <option key={item.codigo} value={item.codigo}>
                            {item.codigo} - {item.descripcion}
                          </option>
                        ))
                      ) : (
                        <option value={formData.ocupacion_cod || "251101"}>
                          Sin resultados para el filtro actual
                        </option>
                      )}
                    </Form.Select>
                    <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                      Seleccione una ocupación del catálogo SUNAT para T-Registro.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <div className="small text-muted">
                Los campos técnicos de SUNAT se gestionan en <b>Configuración T-Registro</b>.
              </div>
            </Card.Body>
          </Card>

          {/* Configuración T-Registro */}
          <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #6f42c1 0%, #6610f2 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaIdCard size={16} /> Configuración T-Registro
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Accordion defaultActiveKey={["0"]} alwaysOpen>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>IDE - Identificación</Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>País Emisor del Documento</Form.Label>
                          <Form.Select name="pais_emisor_cod" value={formData.pais_emisor_cod} onChange={handleChange}>
                            {OPCIONES_PAISES.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Nacionalidad</Form.Label>
                          <Form.Select name="nacionalidad_cod" value={formData.nacionalidad_cod} onChange={handleChange}>
                            {OPCIONES_PAISES.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>TRI - Datos Laborales</Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-3">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Régimen Laboral</Form.Label>
                          <Form.Select name="regimen_laboral_cod" value={formData.regimen_laboral_cod} onChange={handleChange}>
                            {OPCIONES_REGIMEN_LABORAL.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Categoría del Trabajador</Form.Label>
                          <Form.Select name="categoria_trabajador_cod" value={formData.categoria_trabajador_cod} onChange={handleChange}>
                            {OPCIONES_CATEGORIA_TRABAJADOR.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Nivel Educativo</Form.Label>
                          <Form.Select name="nivel_educativo_cod" value={formData.nivel_educativo_cod} onChange={handleChange}>
                            {OPCIONES_NIVEL_EDUCATIVO.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Tipo de Contrato</Form.Label>
                          <Form.Select name="tipo_contrato_cod" value={formData.tipo_contrato_cod} onChange={handleChange}>
                            {OPCIONES_TIPO_CONTRATO.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Discapacidad</Form.Label>
                          <Form.Select name="discapacidad_cod" value={formData.discapacidad_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">Sí</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>SCTR Salud</Form.Label>
                          <Form.Select name="sctr_salud_cod" value={formData.sctr_salud_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">EsSalud</option>
                            <option value="2">EPS</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>SCTR Pensión</Form.Label>
                          <Form.Select name="sctr_pension_cod" value={formData.sctr_pension_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">ONP</option>
                            <option value="2">AFP</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Horario Nocturno</Form.Label>
                          <Form.Select name="horario_nocturno_cod" value={formData.horario_nocturno_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">Sí</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Jornada Máxima Legal</Form.Label>
                          <Form.Select name="regimen_jornada_laboral_cod" value={formData.regimen_jornada_laboral_cod} onChange={handleChange}>
                            <option value="1">Sí</option>
                            <option value="0">No</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Sujeto a Fiscalización Horaria</Form.Label>
                          <Form.Select name="sujeto_fiscalizacion_horario_cod" value={formData.sujeto_fiscalizacion_horario_cod} onChange={handleChange}>
                            <option value="1">Sí</option>
                            <option value="0">No</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Sindicalizado</Form.Label>
                          <Form.Select name="sindicalizado_cod" value={formData.sindicalizado_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">Sí</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Periodicidad de Ingresos</Form.Label>
                          <Form.Select name="periodicidad_ingresos_cod" value={formData.periodicidad_ingresos_cod} onChange={handleChange}>
                            {OPCIONES_PERIODICIDAD_INGRESOS.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Percibe Renta 5ta</Form.Label>
                          <Form.Select name="percibe_renta5ta_cod" value={formData.percibe_renta5ta_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">Sí</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Ocupación Pública</Form.Label>
                          <Form.Select name="ocupacion_publica_cod" value={formData.ocupacion_publica_cod} onChange={handleChange}>
                            <option value="0">No</option>
                            <option value="1">Sí</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>PER - Periodo</Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Categoría para Periodo</Form.Label>
                          <Form.Select
                            name="categoria_periodo_per_cod"
                            value={formData.categoria_periodo_per_cod}
                            onChange={handleChange}
                          >
                            <option value="01">Trabajador</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Card.Body>
          </Card>

          {/* Régimen Pensionario */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaShieldAlt size={16} /> Régimen Pensionario
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Régimen Pensionario</Form.Label>
                    <Form.Select
                      name="regimen_pensionario"
                      value={formData.regimen_pensionario}
                      onChange={handleChange}
                    >
                      <option value="ONP">ONP</option>
                      <option value="AFP">AFP</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>AFP</Form.Label>
                    <Form.Select
                      name="afp"
                      value={formData.afp}
                      onChange={handleChange}
                      disabled={formData.regimen_pensionario === "ONP"}
                    >
                      <option value="">Seleccione AFP</option>
                      <option value="INTEGRA">INTEGRA</option>
                      <option value="PROFUTURO">PROFUTURO</option>
                      <option value="PRIMA">PRIMA</option>
                      <option value="HABITAT">HABITAT</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>CUSPP</Form.Label>
                    <Form.Control
                      type="text"
                      name="cuspp"
                      value={formData.cuspp}
                      onChange={handleChange}
                      disabled={formData.regimen_pensionario === "ONP"}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Fecha Término Comisión AFP</Form.Label>
                    <Form.Control
                      type="text"
                      name="fecha_termino_comision_afp"
                      value={formData.fecha_termino_comision_afp}
                      onChange={handleChange}
                      placeholder="DD/MM/YYYY"
                      disabled={formData.regimen_pensionario === "ONP"}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Información Salarial */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaMoneyBillWave size={16} /> Información Salarial
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Sueldo Base</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>S/</InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="sueldo_base"
                        value={formData.sueldo_base}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Asignación Familiar</Form.Label>
                    <Form.Check
                      type="checkbox"
                      name="asignacion_familiar"
                      checked={formData.asignacion_familiar}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Bonos Asignados */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaGift size={16} /> Bonos Asignados
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Form.Text className="text-muted mb-3">
                Asigna bonos globales al empleado con montos específicos.
              </Form.Text>
              {formData.bonos_asignados?.map((bono, index) => (
                <Row key={index} className="mb-3 align-items-end">
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>Bono Global</Form.Label>
                      <Form.Select
                        value={bono.bonoId}
                        onChange={(e) =>
                          handleBonoAsignadoChange(
                            index,
                            "bonoId",
                            e.target.value
                          )
                        }
                      >
                        <option value="">Seleccione bono</option>
                        {bonosGlobales.map((bg) => (
                          <option key={bg.id} value={bg.id}>
                            {bg.tipo_bono} - {bg.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Monto</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>S/</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={bono.monto}
                          onChange={(e) =>
                            handleBonoAsignadoChange(
                              index,
                              "monto",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          step="0.01"
                          min="0"
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Button
                      variant="outline-danger"
                      onClick={() => removeBonoAsignado(index)}
                    >
                      Eliminar
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button variant="outline-primary" onClick={addBonoAsignado}>
                Agregar Bono Asignado
              </Button>
            </Card.Body>
          </Card>

          {/* Información Organizacional */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaBuilding size={16} /> Información Organizacional
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Centro de Costo</Form.Label>
                    <Form.Select
                      name="centro_costo"
                      value={formData.centro_costo}
                      onChange={handleChange}
                    >
                      <option value="ADMINISTRATIVO">ADMINISTRATIVO</option>
                      <option value="VENTAS">VENTAS</option>
                      <option value="PRODUCCION">PRODUCCIÓN</option>
                      <option value="LOGISTICA">LOGÍSTICA</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Categoría</Form.Label>
                    <Form.Control
                      type="text"
                      name="categoria"
                      value={formData.categoria}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Área</Form.Label>
                    <Form.Control
                      type="text"
                      name="area"
                      value={formData.area}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Información Bancaria y Contacto */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaEnvelope size={16} /> Información Bancaria y Contacto
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Cuenta Bancaria</Form.Label>
                    <Form.Control
                      type="text"
                      name="cuenta_bancaria"
                      value={formData.cuenta_bancaria}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Banco</Form.Label>
                    <Form.Control
                      type="text"
                      name="banco"
                      value={formData.banco}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaEnvelope className="me-1" />
                      Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      <FaPhone className="me-1" />
                      Teléfono
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Seguros */}
          <Card
            className="shadow-sm border-0 mb-4"
            style={{ borderRadius: "15px" }}
          >
            <Card.Header
              className="text-white py-3"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <FaShieldAlt size={16} /> Seguros
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>ESSALUD Vida Importe</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>S/</InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="essalud_vida_importe"
                        value={formData.essalud_vida_importe}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Vida Ley Importe</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>S/</InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="vida_ley_importe"
                        value={formData.vida_ley_importe}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>SCTR</Form.Label>
                    <Form.Control
                      type="text"
                      name="sctr"
                      value={formData.sctr}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha EMO Inicio
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_EMO_inicio"
                      value={formData.fecha_EMO_inicio}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha EMO Fin
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_EMO_fin"
                      value={formData.fecha_EMO_fin}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha OEMO Inicio
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_OEMO_inicio"
                      value={formData.fecha_OEMO_inicio}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha OEMO Fin
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_OEMO_fin"
                      value={formData.fecha_OEMO_fin}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      <FaCalendar className="me-1" />
                      Fecha Brevete Fin
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_brevete_fin"
                      value={formData.fecha_brevete_fin}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer
          className="p-4"
          style={{ backgroundColor: "#f8f9fa", borderTop: "3px solid #D2691E" }}
        >
          <Button
            variant="outline-secondary"
            onClick={onHide}
            className="px-4 py-2"
            style={{ borderRadius: "8px" }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className="px-4 py-2"
            style={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              border: "none",
              boxShadow: "0 2px 4px rgba(139, 69, 19, 0.3)",
            }}
          >
            {loading
              ? "Guardando..."
              : empleado
              ? "Actualizar Empleado"
              : "Crear Empleado"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmpleadoForm;
